/**
 * تبويب "الأصناف" — فئات وأصناف، بإنشاء وتعديل وحذف كاملين لمدير النظام
 */
import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api from "../../api/client";
import { useItemCategories } from "../../hooks/useItems";
import { ITEM_UNIT_LABELS } from "../../types";
import type { ItemUnit, ItemCategory, Item } from "../../types";

const UNITS = Object.keys(ITEM_UNIT_LABELS) as ItemUnit[];
const DECIMAL_OPTIONS = [
  { value: 0, label: "عدد صحيح (بلا فواصل)" },
  { value: 1, label: "منزلة عشرية واحدة على الأكثر" },
  { value: 2, label: "منزلتان عشريتان على الأكثر" },
];

interface CatForm { nameAr: string; nameEn: string; sortOrder: string }
interface ItemForm {
  categoryId: string; code: string; nameAr: string; nameEn: string;
  unit: ItemUnit; maxDecimalPlaces: number; sortOrder: string; perPersonRatio: string;
}
const emptyCat: CatForm = { nameAr: "", nameEn: "", sortOrder: "" };
const emptyItem = (categoryId = ""): ItemForm =>
  ({ categoryId, code: "", nameAr: "", nameEn: "", unit: "MEAL", maxDecimalPlaces: 0, sortOrder: "", perPersonRatio: "" });

export default function ItemsTab() {
  const qc = useQueryClient();
  const { data: categories = [], isLoading } = useItemCategories(false);

  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  const [catMode, setCatMode]   = useState<"new" | number | null>(null);
  const [catForm, setCatForm]   = useState<CatForm>(emptyCat);
  const [itemMode, setItemMode] = useState<"new" | number | null>(null);
  const [itemForm, setItemForm] = useState<ItemForm>(emptyItem());

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["item-categories"] });
    qc.invalidateQueries({ queryKey: ["items"] });
  };

  const run = async (fn: () => Promise<unknown>, okMsg: string) => {
    setError(""); setSuccess("");
    try { await fn(); refresh(); setSuccess(okMsg); return true; }
    catch (err: unknown) {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "حدث خطأ"
      );
      return false;
    }
  };

  // ── الفئات ──────────────────────────────────────────────────────────────
  const openNewCat  = () => { setCatMode("new"); setCatForm(emptyCat); setItemMode(null); };
  const openEditCat = (c: ItemCategory) => {
    setCatMode(c.id); setCatForm({ nameAr: c.nameAr, nameEn: c.nameEn ?? "", sortOrder: String(c.sortOrder) });
    setItemMode(null);
  };
  const submitCat = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      nameAr: catForm.nameAr.trim(), nameEn: catForm.nameEn.trim() || null,
      sortOrder: catForm.sortOrder ? parseInt(catForm.sortOrder, 10) : 0,
    };
    const ok = catMode === "new"
      ? await run(() => api.post("/item-categories", payload), "تمت إضافة الفئة")
      : await run(() => api.put(`/item-categories/${catMode}`, payload), "تم تعديل الفئة");
    if (ok) setCatMode(null);
  };
  const toggleCategory = (c: ItemCategory) =>
    void run(() => api.put(`/item-categories/${c.id}`, { isActive: !c.isActive }),
      c.isActive ? "تم تعطيل الفئة وأصنافها" : "تم تفعيل الفئة");
  const deleteCategory = (c: ItemCategory) => {
    if (!confirm(`حذف فئة "${c.nameAr}" نهائياً؟ لا يمكن التراجع.`)) return;
    void run(() => api.delete(`/item-categories/${c.id}`), "تم حذف الفئة");
  };

  // ── الأصناف ─────────────────────────────────────────────────────────────
  const openNewItem  = (categoryId: number) => { setItemMode("new"); setItemForm(emptyItem(String(categoryId))); setCatMode(null); };
  const openEditItem = (it: Item) => {
    setItemMode(it.id);
    setItemForm({
      categoryId: String(it.categoryId), code: it.code, nameAr: it.nameAr, nameEn: it.nameEn ?? "",
      unit: it.unit, maxDecimalPlaces: it.maxDecimalPlaces, sortOrder: String(it.sortOrder),
      perPersonRatio: it.perPersonRatio != null ? String(it.perPersonRatio) : "",
    });
    setCatMode(null);
  };
  const submitItem = async (e: React.FormEvent) => {
    e.preventDefault();
    const base = {
      nameAr: itemForm.nameAr.trim(), nameEn: itemForm.nameEn.trim() || null,
      unit: itemForm.unit, maxDecimalPlaces: itemForm.maxDecimalPlaces,
      sortOrder: itemForm.sortOrder ? parseInt(itemForm.sortOrder, 10) : 0,
      perPersonRatio: itemForm.perPersonRatio.trim() ? parseFloat(itemForm.perPersonRatio) : null,
    };
    const ok = itemMode === "new"
      ? await run(() => api.post("/items", {
          ...base, categoryId: parseInt(itemForm.categoryId, 10), code: itemForm.code.trim().toUpperCase(),
        }), "تمت إضافة الصنف")
      : await run(() => api.put(`/items/${itemMode}`, base), "تم تعديل الصنف");
    if (ok) setItemMode(null);
  };
  const toggleItem = (it: Item) =>
    void run(() => api.put(`/items/${it.id}`, { isActive: !it.isActive }),
      it.isActive ? "تم تعطيل الصنف" : "تم تفعيل الصنف");
  const deleteItem = (it: Item) => {
    if (!confirm(`حذف صنف "${it.nameAr}" نهائياً؟ لا يمكن التراجع.`)) return;
    void run(() => api.delete(`/items/${it.id}`), "تم حذف الصنف");
  };

  const catFormBlock = (
    <form onSubmit={submitCat} className="card mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
      <div>
        <label className="label">اسم الفئة (عربي)</label>
        <input className="input" required value={catForm.nameAr}
          onChange={(e) => setCatForm({ ...catForm, nameAr: e.target.value })} />
      </div>
      <div>
        <label className="label">الاسم (إنجليزي)</label>
        <input className="input" value={catForm.nameEn}
          onChange={(e) => setCatForm({ ...catForm, nameEn: e.target.value })} />
      </div>
      <div>
        <label className="label">الترتيب</label>
        <input className="input" type="number" min="0" value={catForm.sortOrder}
          onChange={(e) => setCatForm({ ...catForm, sortOrder: e.target.value })} />
      </div>
      <div className="flex gap-2">
        <button type="submit" className="btn-primary flex-1">{catMode === "new" ? "حفظ الفئة" : "حفظ التعديل"}</button>
        <button type="button" className="btn-secondary" onClick={() => setCatMode(null)}>إلغاء</button>
      </div>
    </form>
  );

  const itemFormBlock = (
    <form onSubmit={submitItem} className="card mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
      <div>
        <label className="label">الفئة</label>
        <select className="input" required value={itemForm.categoryId} disabled={itemMode !== "new"}
          onChange={(e) => setItemForm({ ...itemForm, categoryId: e.target.value })}>
          <option value="">-- اختر الفئة --</option>
          {categories.filter((c) => c.isActive || String(c.id) === itemForm.categoryId).map((c) => (
            <option key={c.id} value={c.id}>{c.nameAr}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="label">الرمز {itemMode !== "new" && "(لا يُعدَّل)"}</label>
        <input className="input" required placeholder="RICE" dir="ltr" disabled={itemMode !== "new"}
          pattern="[A-Za-z0-9_]{2,60}" value={itemForm.code}
          onChange={(e) => setItemForm({ ...itemForm, code: e.target.value.toUpperCase() })} />
      </div>
      <div>
        <label className="label">الاسم (عربي)</label>
        <input className="input" required value={itemForm.nameAr}
          onChange={(e) => setItemForm({ ...itemForm, nameAr: e.target.value })} />
      </div>
      <div>
        <label className="label">وحدة القياس</label>
        <select className="input" value={itemForm.unit}
          onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value as ItemUnit })}>
          {UNITS.map((u) => <option key={u} value={u}>{ITEM_UNIT_LABELS[u]}</option>)}
        </select>
      </div>
      <div>
        <label className="label">دقة الكمية</label>
        <select className="input" value={itemForm.maxDecimalPlaces}
          onChange={(e) => setItemForm({ ...itemForm, maxDecimalPlaces: parseInt(e.target.value, 10) })}>
          {DECIMAL_OPTIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
      </div>
      <div>
        <label className="label">نسبة استحقاق الفرد (اختياري)</label>
        <input type="number" min="0" step="0.01" className="input" dir="ltr"
          placeholder="مثال: 0.5 لخبز (ربطة لكل شخصين)"
          value={itemForm.perPersonRatio}
          onChange={(e) => setItemForm({ ...itemForm, perPersonRatio: e.target.value })} />
        <p className="text-[10px] text-sand-500 mt-1">
          تُستخدم فقط لاقتراح رقم عند ضبط معايير الاستحقاق — لا تُفرض تلقائياً
        </p>
      </div>
      <div className="flex gap-2">
        <button type="submit" className="btn-primary flex-1">{itemMode === "new" ? "حفظ الصنف" : "حفظ التعديل"}</button>
        <button type="button" className="btn-secondary" onClick={() => setItemMode(null)}>إلغاء</button>
      </div>
    </form>
  );

  return (
    <div>
      {error   && <div className="alert-error   mb-4">{error}</div>}
      {success && <div className="alert-success mb-4">{success}</div>}

      <div className="flex justify-end mb-3">
        <button className="btn-secondary" onClick={openNewCat}>+ فئة جديدة</button>
      </div>

      {catMode === "new" && catFormBlock}
      {isLoading && <p className="text-sm text-sand-500">جاري التحميل...</p>}

      <div className="space-y-4">
        {categories.map((cat) => (
          <div key={cat.id} className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className={`font-semibold ${cat.isActive ? "" : "text-sand-400 line-through"}`}>
                {cat.nameAr}
                <span className="text-xs text-sand-400 mr-2">({cat.items?.length ?? 0} صنف)</span>
              </h3>
              <div className="flex gap-2 text-xs">
                <button className="text-primary-700 hover:underline" onClick={() => openEditCat(cat)}>تعديل</button>
                <button className="text-primary-700 hover:underline" onClick={() => toggleCategory(cat)}>
                  {cat.isActive ? "تعطيل" : "تفعيل"}
                </button>
                <button className="text-[#6b1f2a] hover:underline" onClick={() => deleteCategory(cat)}>حذف</button>
                <button className="btn-secondary !py-1 !px-2" onClick={() => openNewItem(cat.id)}>+ صنف</button>
              </div>
            </div>

            {catMode === cat.id && catFormBlock}

            <table className="w-full text-sm">
              <thead>
                <tr className="text-sand-500 text-xs border-b border-sand-200">
                  <th className="text-right py-1">الصنف</th>
                  <th className="text-right py-1">الرمز</th>
                  <th className="text-right py-1">الوحدة</th>
                  <th className="text-right py-1">الدقة</th>
                  <th className="text-right py-1">الحالة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(cat.items ?? []).map((it) => (
                  <React.Fragment key={it.id}>
                    <tr className="border-b border-sand-100 last:border-0">
                      <td className={`py-1 ${it.isActive ? "" : "text-sand-400 line-through"}`}>{it.nameAr}</td>
                      <td className="py-1 text-sand-400" dir="ltr">{it.code}</td>
                      <td className="py-1">{ITEM_UNIT_LABELS[it.unit]}</td>
                      <td className="py-1">{it.maxDecimalPlaces === 0 ? "صحيح" : `${it.maxDecimalPlaces} عشري`}</td>
                      <td className="py-1">{it.isActive ? "مفعّل" : "معطّل"}</td>
                      <td className="py-1 text-left whitespace-nowrap">
                        <button className="text-xs text-primary-700 hover:underline ml-3" onClick={() => openEditItem(it)}>تعديل</button>
                        <button className="text-xs text-primary-700 hover:underline ml-3" onClick={() => toggleItem(it)}>
                          {it.isActive ? "تعطيل" : "تفعيل"}
                        </button>
                        <button className="text-xs text-[#6b1f2a] hover:underline" onClick={() => deleteItem(it)}>حذف</button>
                      </td>
                    </tr>
                    {itemMode === it.id && (
                      <tr><td colSpan={6}>{itemFormBlock}</td></tr>
                    )}
                  </React.Fragment>
                ))}
                {itemMode === "new" && itemForm.categoryId === String(cat.id) && (
                  <tr><td colSpan={6}>{itemFormBlock}</td></tr>
                )}
                {(cat.items?.length ?? 0) === 0 && itemMode !== "new" && (
                  <tr><td colSpan={6} className="py-2 text-sand-400 text-xs">لا توجد أصناف في هذه الفئة</td></tr>
                )}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
